/* eslint-disable react/no-danger */
import * as React from 'react';
// import Tags from '@yaireo/tagify/dist/react.tagify';
import { MixedTags } from '@yaireo/tagify/dist/react.tagify';
import './tagify.scss';
import { useIntl } from 'react-intl';
import { useQueries } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { CmdError } from '@commander-services/gui-components';
import CmdSimpleSelectButton from '@commander-services/cmd-simple-select-button';
import { IAlarmValidationNotificationMessageApiResponse } from '../../Alarms/types';
import { rideOwnershipValueAtom, ridePrivacyValueAtom } from '../../Alarms/AlarmState';
import { selectConfigurations, sortOptionsByKeys, tagsTitles } from '../../Alarms/AlarmRuleService';
import { getNotificationMessageItems } from '../../Alarms/AlarmService';

interface IContentEditableTextareaProps {
  // allowedHtml?: string[];
  // id: string;
  name: string;
  required?: boolean;
  label: string;
  // width?: string;
  // placeholder?: string;
  // height?: string;
  // options: ITagifyItem[];
  // rows?: number;
  // note?: string;
  // tooltip?: string;
  value: string;
  error: any;
  onChangeCallback: (value: string) => void;
  gdprValidation: string[];
  warningMessage?: string;
  handleBusinessValidation: () => void;
}

export interface ITagifyItem {
  id?: string;
  title: string;
  value: string;
  disabled: boolean;
  item?: string;
}

export default function ContentEditableTextarea(props: IContentEditableTextareaProps): JSX.Element {
  const tagifyRef = React.useRef<Tagify | undefined>();
  const [content, setContent] = React.useState<string>('');
  const [tagifyClass, setTagifyClass] = React.useState<string>('xxx');
  const lastTagValue = React.useRef('');
  const [options, setOptions] = React.useState<ITagifyItem[]>([]);
  const { formatMessage: f } = useIntl();
  const rideOwnershipValue = useRecoilValue<string>(rideOwnershipValueAtom);
  const ridePrivacyValue = useRecoilValue<string>(ridePrivacyValueAtom);

  const getMessageBodyItemsAsync = async () => {
    const response: IAlarmValidationNotificationMessageApiResponse | null =
      await getNotificationMessageItems(rideOwnershipValue, ridePrivacyValue);
    if (response) {
      const tagifyOptions: ITagifyItem[] = Object.values(response).map((i: any) => {
        i.title = i.item;
        i.id = i.value;
        return i;
      });

      setOptions(tagifyOptions);

      return tagifyOptions;
    }

    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['tagifyMessageOptions', props.gdprValidation],
        queryFn: () => getMessageBodyItemsAsync(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!props.gdprValidation,
      },
    ],
  });
  const { data: tagifyOptions, isLoading: isTagifyOptions } = results[0];

  const fromTagify = (message: string): string => {
    const regex = /\[\[\{.*?"value":"(.*?)".*?(,"title":"(.*?)")?[^}]*\}\]\]/g;

    return message.replace(regex, (match, value, _, title) => {
      // If title is missing or empty, find it in props.options
      const tagTitle =
        title ||
        f({
          id: `${tagsTitles.find((option) => option.value === value)?.item || ''}`,
        }) ||
        value;

      return `@[${tagTitle}](${value})`;
    });
  };

  // const toTagify = (message: string) => {
  //   const regex = /@\[(.*?)\]\((.*?)\)/gm;
  //   const subst = `[[{"value":"$2","title":"$1","prefix":"#","id":"$2"}]]`;
  //   let result = message.replace(regex, subst);

  //   // Handle the case where the tag structure is [[[...]]]
  //   const badStructureRegex = /\[\[\[(.*?)\]\]\]/g;
  //   result = result.replace(badStructureRegex, '[[$1]]');

  //   return result;
  // };
  const toTagify = (message: string) => {
    // Define the regex to capture the title and value within the tag structure
    const regex = /@\[(.*?)\]\((.*?)\)/gm;

    let result = message.replace(regex, (match, title, value) => {
      // Check if the value matches any entry in AlarmRuleService.tagsTitles
      const matchingOption = tagsTitles.find((option) => option.value === value);

      if (matchingOption) {
        // Proceed with the replacement if a match is found
        return `[[{"value":"${value}","title":"${f({
          id: `${matchingOption.item}`,
        })}","prefix":"#","id":"${value}"}]]`;
      }
      // Return the original match without transformation if no match is found
      return match;
    });

    // Handle potential bad structure (triple-bracketed) in the transformed result
    const badStructureRegex = /\[\[\[(.*?)\]\]\]/g;
    result = result.replace(badStructureRegex, '[[$1]]');

    return result;
  };

  const handleInvalidTags = (tags: string[]) => {
    tags.forEach((tagValue: string) => {
      const items = document.querySelectorAll(`[data-tag-value="${tagValue}"]`);
      items.forEach((item: any) => {
        const text = item.querySelector('.tagify__tag-text');
        const icon = item.querySelector('.tagify__tag__removeBtn');
        // Apply the style changes
        item.style.background = '#fff';
        item.style.borderRadius = '3px';
        item.style.borderColor = '#b50536';
        if (text && icon) {
          text.style.color = '#b50536';
          icon.style.color = '#b50536';
          // Add the exclamation icon if not already present
          if (!text.children[0]) {
            const para = document.createElement('i');
            const node = document.createTextNode(' ');
            para.appendChild(node);
            para.classList.add('cmd-icon');
            para.classList.add('cmd-icon-exclamation');
            para.style.cssText = 'position: relative; top: -1px;';
            text.prepend(para);
          }
        }
      });
    });
  };

  const handleValidTags = (tags: string[]) => {
    tags.forEach((tagValue: string) => {
      const items = document.querySelectorAll(`[data-tag-value="${tagValue}"]`);
      items.forEach((item: any) => {
        if (!props.gdprValidation.includes(tagValue)) {
          // Apply reset styles only if the tagValue is NOT included in props.gdprValidation
          item.style.background = '#fff'; // Reset background to default white
          item.style.borderRadius = '3px'; // Reset border radius to '3px'
          item.style.borderColor = '#494949';
          const text = item.querySelector('.tagify__tag-text');
          const icon = item.querySelector('.tagify__tag__removeBtn');
          if (text && icon) {
            text.style.color = '#494949'; // Reset text color
            icon.style.color = '#494949'; // Reset icon color
            // Remove any exclamation icon or specific indicator for the invalid state, if previously added
            const para = text.querySelector('.cmd-icon');
            if (para) {
              para.remove();
            }
          }
        }
      });
    });
  };

  // React.useEffect(() => {
  //   if (tagifyRef && tagifyRef.current) {
  //     (tagifyRef.current as any).settings.whitelist = options.filter(
  //       (i: ITagifyItem) => !i.disabled
  //     );
  //   }
  // }, [options]);

  React.useEffect(() => {
    // Handle tagify setup or other effects here
    // For instance, updating the tagify instance with new options if needed:
    if (tagifyRef.current && options.length > 0) {
      tagifyRef.current.settings.whitelist = options;
      setContent(toTagify(props.value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, props.value]);

  React.useEffect(() => {
    // setContent(toTagify(props.value));
    setTagifyClass('form-control-content-editable');
    const tagsInputDiv = document.querySelector('.tags-input');
    if (tagsInputDiv) {
      // Add attribute 'data-cy' to the tags-input div
      tagsInputDiv.setAttribute('data-cy', 'text-area-tagify');
    }
  }, [props.value]);

  React.useEffect(() => {
    if (props.gdprValidation && props.gdprValidation.length > 0) {
      handleInvalidTags(props.gdprValidation);
    }
    if (tagifyRef.current) {
      const tagValues = tagifyRef?.current?.value.map((tag) => tag.value) || [];

      handleValidTags(tagValues);

      // const tagValues = tagifyRef?.current?.value.map((tag) => tag.value) || [];
      // handleValidTags(tagValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.gdprValidation, options]);

  const handleOnChange = (event: any) => {
    if (event.detail.tagify.state.lastOriginalValueReported) {
      // const tagValues = tagifyRef?.current?.value.map((tag) => tag.value) || [];
      // handleValidTags(tagValues);
      props.onChangeCallback(fromTagify(event.detail.tagify.state.lastOriginalValueReported));
    }
  };

  // const handleOnAdd = (event: any) => {
  //   handleValidTags(props.options.map((item: ITagifyItem) => item.value));
  //   props.onChangeCallback(fromTagify(event.detail.tagify.state.lastOriginalValueReported));
  // };

  const handleRemove = (event: any) => {
    props.onChangeCallback(fromTagify(event.detail.tagify.state.lastOriginalValueReported));
    props.handleBusinessValidation();
  };

  const handleSelectTag = (option: ITagifyItem) => {
    if (tagifyRef.current) {
      tagifyRef.current.addTags([option]); // Add the new tag to the Tagify instance

      if (props.gdprValidation) {
        handleInvalidTags(props.gdprValidation);
      }
    } else {
      console.error('Tagify instance is not available');
    }
  };

  // const handleSelectTag = (option: ITagifyItem) => {
  //   if (tagifyRef.current) {
  //     const currentValue = tagifyRef.current.DOM.originalInput.value;
  //     const tagString = `@[${option.title}](${option.value}) `;
  //     // Append a newline character before the tag
  //     const newContent = toTagify(`${currentValue}\u00A0${tagString}`).trim();
  //     setContent(newContent);
  //     tagifyRef.current.loadOriginalValues(newContent);

  //     if (props.gdprValidation) {
  //       handleInvalidTags(props.gdprValidation);
  //     }
  //     handleValidTags(tagifyRef.current.value.map((tag) => tag.value));
  //   }
  // };

  const handleFocus = () => {
    setTimeout(() => {
      if (tagifyRef.current) {
        addSpaceAfterLastTag();
      }
    }, 50);
  };

  const tagTemplate = (tagData) => {
    handleFocus();
    lastTagValue.current = tagData.__tagId;
    let tagTitle = tagData.title || options.find((i) => i.value === tagData.value)?.title || '';

    if (!tagTitle && options.length === 0) {
      const tag = tagsTitles.find((i) => i.value === tagData.value);
      tagTitle = tag?.item || tagData.value;
    }

    return `
        <tag style="margin: 0 4px 8px 4px; height: 22px; border-radius: 4px" title="${tagTitle}" key="${tagData.__tagId}" id="${tagData.item}" data-cy="${
          tagData.value
        }"
                contenteditable='false'
                spellcheck='false'
                tabIndex="-1"
                class="tagify__tag ${tagData.class ? tagData.class : ''}"
                data-tag-value="${tagData.value}"
                ${tagifyRef?.current?.getAttributes(tagData)}>
            <a title='' class='tagify__tag__removeBtn cmd-icon cmd-icon-remove-sm' role='button' aria-label='remove tag' data-cy="remove-btn-${
              tagData.value
            }" ></a>
            <div>
                <span class='tagify__tag-text' data-cy="title-${tagData.value}">${tagTitle}</span>
            </div>
        </tag>
    `;
  };

  const placeCaretAfterNode = (node) => {
    const range = document.createRange();
    const sel = window.getSelection();
    range.setStartAfter(node);
    range.collapse(true);
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  const addSpaceAfterLastTag = () => {
    const tagElements: Element[] = Array.from(
      tagifyRef.current.DOM.scope.querySelectorAll('.tagify__tag')
    );
    if (tagElements.length > 0) {
      const lastTag = tagElements.find((tag) => tag.getAttribute('key') === lastTagValue.current);

      if (lastTag) {
        const nextSibling = lastTag.nextSibling;
        if (nextSibling && nextSibling.nodeType === 3 && nextSibling.nodeValue === '\u00A0') {
          placeCaretAfterNode(nextSibling);
        } else {
          const spaceNode = document.createTextNode('\u00A0');
          lastTag.parentNode?.insertBefore(spaceNode, nextSibling);
          placeCaretAfterNode(spaceNode);
        }
      }
    }
  };

  const handleSelectOption = (option: ITagifyItem) => {
    handleSelectTag(option);
    props.handleBusinessValidation();
  };

  const renderSimpleSelects = () => {
    return selectConfigurations.map((config, index) => (
      <CmdSimpleSelectButton
        id={config.id}
        key={index}
        options={sortOptionsByKeys(options.filter(config.filter), config.keys)}
        onSelect={handleSelectOption}
        title={f({ id: config.title })}
      />
    ));
  };

  return (
    <div className="form-group">
      <div>
        <label className="d-block form-label">
          {props.required && <span className="text-danger required-field-info">*</span>}
          {props.label}
          {/* {props.note && (
            <>
              <br />
              <small dangerouslySetInnerHTML={{ __html: props.note }} />
            </>
          )} */}
        </label>
        <div className="d-block">
          <div
            className={`${props.warningMessage ? 'form-group-warning' : ''} ${
              props.error ? 'form-group-error' : ''
            }`}
          >
            <div className="b-textarea-select-picker">
              <div className="mb-2">{f({ id: 'alarm.form.notificationMessageTags.text' })}</div>
              <div className="row no-gutters">{renderSimpleSelects()}</div>
            </div>

            <MixedTags
              tagifyRef={tagifyRef}
              name={props.name}
              onChange={handleOnChange}
              onRemove={handleRemove}
              settings={{
                editTags: false,
                mode: 'mix',
                // pattern: /#/,
                duplicates: true,
                enforceWhitelist: true,
                whitelist: [],
                blacklist: [],
                mixMode: {
                  insertAfterTag: '\u00A0',
                },
                // dropdown: {
                //   maxItems: 100,
                //   enabled: 0,
                //   position: 'text',
                //   highlightFirst: true,
                //   searchKeys: ['title'],
                //   mapValueTo: (data: any) => data.title,
                // },
                // callbacks: {
                //   // add: handleOnAdd,
                //   remove: handleRemove,
                //   change: handleOnChange,
                // },
                templates: {
                  tag: tagTemplate,
                  // dropdownItem(item: any) {
                  //   if (!item.disabled && tagifyRef && tagifyRef.current) {
                  //     return `<div ${(tagifyRef.current as any).getAttributes(item)}
                  //     class='${(tagifyRef.current as any).settings.classNames.dropdownItem} ${
                  //       item.class ? item.class : ''
                  //     }'
                  //     tabindex="0"
                  //     role="option">${item.title}
                  //   </div>`;
                  //   }
                  //   return '';
                  // },
                },
              }}
              // className="form-control-content-editable"
              className={tagifyClass}
              readOnly={false}
              value={content}
            />
          </div>

          {/* <Tags
            name={props.name}
            className="form-control-content-editable textarea"
            InputMode="textarea"
            tagifyRef={tagifyRef} // optional Ref object for the Tagify instance itself, to get access to  inner-methods
            // autofocus={true}
            settings={{
              editTags: false,
              // prefix: '#',
              mode: 'mix',
              pattern: /#/,
              duplicates: true,
              enforceWhitelist: true,
              whitelist: [],
              blacklist: [],
              mixMode: {
                insertAfterTag: '\u00A0',
              },
              dropdown: {
                maxItems: 100,
                enabled: 0,
                position: 'text',
                highlightFirst: true,
                searchKeys: ['title'],
                mapValueTo: (data: any) => data.title,
              },
              callbacks: {
                // add: handleOnAdd,
                remove: handleRemove,
                change: handleOnChange,
              },
              templates: {
                dropdownItem(item: any) {
                  if (!item.disabled && tagifyRef && tagifyRef.current) {
                    return `<div ${(tagifyRef.current as any).getAttributes(item)}
                      class='${(tagifyRef.current as any).settings.classNames.dropdownItem} ${
                      item.class ? item.class : ''
                    }'
                      tabindex="0"
                      role="option">${item.value}
                    </div>`;
                  }
                  return '';
                },
              },
            }}
            value={toTagify(props.value)}
          /> */}
          {props.error && <CmdError message={props.error} />}
          {props.warningMessage && <CmdError message={props.warningMessage} />}
        </div>
      </div>
    </div>
  );
}

// export default React.memo(ContentEditableTextarea);
